/* eslint-disable no-unused-vars */
import axios from "axios";
import { CLIENT_TIMEZONE_HEADER, getClientTimezone } from "../Utils/dateTimeUtils";
import {
  FORGOT_PASSWORD_URL,
  LOGIN_URL,
  LOGOUT_URL,
  OTP_VERIFICATION_URL,
  REFRESH_TOKEN_URL,
  REGISTER_URL,
  SET_NEW_PASSWORD_URL,
} from "./endpoints";
const ENV = import.meta.env;

const token = () => localStorage.getItem("token");
export const baseURL = ENV.VITE_REACT_APP_API_URL;

export const imagePreviewURL = ENV.VITE_IMAGE_URL;

const axiosInstance = axios.create({ baseURL: baseURL });

function withClientTimezoneHeader(headers = {}) {
  return {
    ...headers,
    [CLIENT_TIMEZONE_HEADER]: getClientTimezone(),
  };
}

axiosInstance.interceptors.request.use((config) => {
  config.headers = withClientTimezoneHeader(config.headers);
  return config;
});

const excludeUrls = [
  LOGIN_URL,
  LOGOUT_URL,
  FORGOT_PASSWORD_URL,
  OTP_VERIFICATION_URL,
  SET_NEW_PASSWORD_URL,
  REGISTER_URL,
  REFRESH_TOKEN_URL,
];

let refreshPromise = null;

const syncAuthTokensToStore = (result) => {
  if (!result?.token) {
    return;
  }
  import("../Store/Store/store.js").then(({ store }) => {
    import("../Store/Slices/authSlice.js").then(({ setAuthTokens }) => {
      store.dispatch(setAuthTokens(result));
    });
  });
};

const persistRefreshedTokens = (result) => {
  if (result?.token) {
    localStorage.setItem("token", result.token);
  }
  if (result?.refreshToken) {
    localStorage.setItem("refreshToken", result.refreshToken);
  }
  if (result?.sessionId) {
    localStorage.setItem("sessionId", result.sessionId);
  }
  syncAuthTokensToStore(result);
};

const redirectToLogin = () => {
  localStorage.clear();
  let redirectPath;
  if (window?.location?.pathname) {
    redirectPath = window?.location?.pathname;
  }
  if (redirectPath && redirectPath !== "/login") {
    window.location.href = "/login?redirect=" + redirectPath;
  } else {
    window.location.href = "/login";
  }
};

const refreshAccessToken = async () => {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    const refreshToken = localStorage.getItem("refreshToken");
    if (!refreshToken) {
      throw new Error("No refresh token");
    }

    const response = await axios.post(baseURL + REFRESH_TOKEN_URL, {
      refreshToken,
    }, {
      headers: withClientTimezoneHeader({
        Accept: "application/json",
      }),
    });
    const result = response?.data?.result;

    persistRefreshedTokens(result);

    if (!result?.token) {
      throw new Error("Refresh response missing access token");
    }

    return result.token;
  })();

  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
};

axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && originalRequest) {
      const requestUrl = originalRequest.url ?? "";

      if (excludeUrls.some((url) => requestUrl.includes(url))) {
        return Promise.reject(error);
      }

      if (requestUrl.includes(REFRESH_TOKEN_URL)) {
        redirectToLogin();
        return Promise.reject(error);
      }

      if (!originalRequest._retry) {
        originalRequest._retry = true;
        try {
          const newToken = await refreshAccessToken();
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return axiosInstance(originalRequest);
        } catch (refreshError) {
          redirectToLogin();
          return Promise.reject(refreshError);
        }
      }

      redirectToLogin();
    }

    return Promise.reject(error);
  },
);

// Define common API methods
const _get = (url, params = {}) => {
  return axiosInstance.get(baseURL + url, {
    params,
    headers: withClientTimezoneHeader({
      Accept: "application/json",
      Authorization: `Bearer ${token()}`,
    }),
    // withCredentials: true,
  });
};

const _delete = (url, params = {}) => {
  return axiosInstance.delete(baseURL + url, {
    params,
    headers: withClientTimezoneHeader({
      Accept: "application/json",
      Authorization: `Bearer ${token()}`,
    }),
    // withCredentials: true,
  });
};

const _put = (url, data = {}, config = {}) => {
  return axiosInstance.put(baseURL + url, data, {
    headers: withClientTimezoneHeader({
      Accept: "application/json",
      Authorization: `Bearer ${token()}`,
    }),
    // withCredentials: true,
  });
};

const _patch = (url, data = {}, config = {}) => {
  return axiosInstance.patch(baseURL + url, data, {
    headers: withClientTimezoneHeader({
      Accept: "application/json",
      Authorization: `Bearer ${token()}`,
    }),
    // withCredentials: true,
  });
};

const _post = async (url, data = {}, config = {}) => {
  return axiosInstance.post(baseURL + url, data, {
    headers: withClientTimezoneHeader({
      Accept: "application/json",
      Authorization: `Bearer ${token()}`,
    }),
    // withCredentials: true,
  });
};
const _postFile = async (url, data = {}, config = {}) => {
  return axiosInstance.post(baseURL + url, data, {
    headers: withClientTimezoneHeader({
      Accept: "multipart/form-data",
      Authorization: `Bearer ${token()}`,
    }),
    // withCredentials: true,
  });
};

const _postFromData = async (url, data = {}, config = {}) => {
  return axiosInstance.post(baseURL + url, data, {
    headers: withClientTimezoneHeader({
      Accept: "application/json",
      Authorization: `Bearer ${token()}`,
    }),
    // withCredentials: true,
  });
};

const _getBlob = (url, params = {}) => {
  return axiosInstance.get(baseURL + url, {
    params,
    headers: withClientTimezoneHeader({
      Authorization: `Bearer ${token()}`,
    }),
    responseType: "blob",
  });
};

// Export API methods
export {
  _get,
  _delete,
  _put,
  _patch,
  _post,
  _postFromData,
  _postFile,
  _getBlob,
};
